import { wrapElement, unwrapElement } from '@/js/util/helpers'

export const PLACEHOLDER_CLASS = 'cm-placeholder'
export const PLACEHOLDER_OVERLAY_CLASS = 'cm-placeholder-overlay'
export const PLACEHOLDER_CONTENT_CLASS = 'cm-placeholder-content'

/**
 * Get the element that should be wrapped by a placeholder.
 */
function getWrappableElement(element: HTMLElement): HTMLElement {
  let wrappedElement = element

  if (element.dataset.wrappableAncestor) {
    wrappedElement = element.closest(element.dataset.wrappableAncestor) ?? element
  }

  return wrappedElement
}

// Control placeholder's visibility based on the target element's visibility
let observer = new IntersectionObserver(
  entries => {
    for (let entry of entries) {
      let placeholder = entry.target.parentNode!
      let overlay = placeholder.querySelector<HTMLElement>(`.${PLACEHOLDER_OVERLAY_CLASS}`)

      if (entry.isIntersecting) {
        overlay?.style.removeProperty('display')
      } else {
        overlay?.style.setProperty('display', 'none')
      }
    }
  },
  {
    root: null,
    threshold: 0,
  },
)

/**
 * Adds a placeholder for a blocked element.
 * @param element
 */
export function addPlaceholder(element: HTMLElement) {
  const serviceId = element.dataset.serviceId
  if (!serviceId) return
  const service = window.obConsentManager.getService(serviceId)
  if (!service) return

  // Wrapper
  const wrapper = document.createElement('div')
  wrapper.classList.add(PLACEHOLDER_CLASS)
  wrapper.setAttribute('data-placeholder-for-service', serviceId)

  // Overlay
  const overlay = document.createElement('div')
  overlay.classList.add(PLACEHOLDER_OVERLAY_CLASS)
  wrapper.appendChild(overlay)

  // Content container
  const content = document.createElement('div')
  content.classList.add(PLACEHOLDER_CONTENT_CLASS)
  overlay.appendChild(content)

  const buttonsContainer = document.createElement('div')
  buttonsContainer.classList.add('cm-buttons')

  // Message
  const message = document.createElement('p')
  message.textContent = (
    window.obConsentManagerConfig.ui.placeholderMessages.notice_blocked ?? 'blocked %s'
  ).replaceAll('%s', service.label)
  content.appendChild(message)

  // Unblock button
  const unblockButton = document.createElement('button')
  unblockButton.classList.add(...window.obConsentManagerFrontendEnv.buttonClasses['consent'])
  unblockButton.textContent =
    window.obConsentManagerConfig.ui.placeholderMessages.action_accept ?? 'accept'
  unblockButton.addEventListener('click', () => {
    window.obConsentManager.giveConsent(serviceId)
  })
  buttonsContainer.appendChild(unblockButton)

  // Preferences button
  const preferencesButton = document.createElement('button')
  preferencesButton.classList.add(
    ...window.obConsentManagerFrontendEnv.buttonClasses['privacy-settings'],
  )
  preferencesButton.textContent =
    window.obConsentManagerConfig.ui.placeholderMessages.action_privacy_settings ??
    'privacy_settings'
  preferencesButton.addEventListener('click', () => {
    if (!window.obConsentManager.ui) {
      if (DEV_MODE) console.warn('UI not initialized on obConsentManager')
      return
    }

    window.obConsentManager.ui.openManager()
  })
  buttonsContainer.appendChild(preferencesButton)

  content.appendChild(buttonsContainer)

  let wrappedElement = getWrappableElement(element)

  observer.observe(wrappedElement)

  wrapElement(wrappedElement, wrapper)
}

/**
 * Removes the placeholder for a blocked element.
 * @param element
 */
export function removePlaceholder(element: HTMLElement) {
  const wrappedElement = getWrappableElement(element)
  if (!wrappedElement.parentElement?.classList.contains(PLACEHOLDER_CLASS)) return

  // Batch DOM updates to prevent unnecessary layout shifts
  window.requestAnimationFrame(() => {
    observer.unobserve(wrappedElement)
    const placeholder = wrappedElement.parentNode as HTMLElement
    const placeholderOverlay = placeholder.querySelector<HTMLElement>(
      `.${PLACEHOLDER_OVERLAY_CLASS}`,
    )
    placeholderOverlay?.remove()
    unwrapElement(placeholder)
  })
}
